import { z } from 'zod';
export const EmailProviderSchema = z.enum(['IMAP', 'MICROSOFT_GRAPH', 'GMAIL']);
export const EmailAccountStatusSchema = z.enum([
    'ACTIVA',
    'ERROR_AUTH',
    'ERROR_TOKEN',
    'DESHABILITADA',
]);
export const EmailAccountStatusLabel = {
    ACTIVA: 'Activa',
    ERROR_AUTH: 'Error de autenticación',
    ERROR_TOKEN: 'Token caducado',
    DESHABILITADA: 'Deshabilitada',
};
export const CreateImapAccountSchema = z.object({
    emailAddress: z.string().email('Email no válido'),
    displayName: z.string().max(200).optional().nullable(),
    imapHost: z.string().min(2).max(255),
    imapPort: z.coerce.number().int().min(1).max(65535),
    imapUseTls: z.boolean().default(true),
    smtpHost: z.string().max(255).optional().nullable(),
    smtpPort: z.coerce.number().int().min(1).max(65535).optional().nullable(),
    smtpUseTls: z.boolean().optional().nullable(),
    password: z.string().min(1, 'La contraseña/app password es obligatoria'),
    initialSyncDays: z.coerce.number().int().min(1).max(3650).default(90),
});
export const UpdateEmailAccountSchema = z.object({
    displayName: z.string().max(200).nullable().optional(),
    syncEnabled: z.boolean().optional(),
});
/**
 * Reconexión de una cuenta existente: vuelve a introducir la contraseña sin
 * perder la cuenta ni los correos ya sincronizados.
 *
 * Hacía falta porque las credenciales se guardan cifradas y son irrecuperables
 * si cambia la clave de cifrado o si el proveedor caduca la app password: la
 * cuenta se quedaba en ERROR_AUTH y la única salida era borrarla y crearla de
 * nuevo. Los datos de servidor son opcionales: si el usuario solo renueva la
 * contraseña, se conservan los que ya había.
 */
export const ReconnectEmailAccountSchema = z.object({
    password: z.string().min(1, 'La contraseña/app password es obligatoria'),
    imapHost: z.string().min(2).max(255).optional(),
    imapPort: z.coerce.number().int().min(1).max(65535).optional(),
    imapUseTls: z.boolean().optional(),
    smtpHost: z.string().max(255).nullable().optional(),
    smtpPort: z.coerce.number().int().min(1).max(65535).nullable().optional(),
    smtpUseTls: z.boolean().nullable().optional(),
    /** Si cambia el buzón, cuántos días hacia atrás se vuelven a descargar. */
    initialSyncDays: z.coerce.number().int().min(1).max(3650).default(90),
});
export const EmailAccountDtoSchema = z.object({
    id: z.string(),
    provider: EmailProviderSchema,
    emailAddress: z.string(),
    displayName: z.string().nullable(),
    status: EmailAccountStatusSchema,
    syncEnabled: z.boolean(),
    imapHost: z.string().nullable(),
    imapPort: z.number().nullable(),
    imapUseTls: z.boolean().nullable(),
    /** Servidor de envío. La pantalla de reconexión lo precarga para no perderlo. */
    smtpHost: z.string().nullable().default(null),
    smtpPort: z.number().nullable().default(null),
    smtpUseTls: z.boolean().nullable().default(null),
    lastSyncAt: z.string().datetime().nullable(),
    lastSyncError: z.string().nullable(),
    messagesCount: z.number().int().optional(),
    createdAt: z.string().datetime(),
});
export const EmailListQuerySchema = z.object({
    q: z.string().max(200).optional(),
    accountId: z.string().optional(),
    isRead: z.enum(['true', 'false']).optional(),
    archivedToClientId: z.string().optional(),
    archivedToOpportunityId: z.string().optional(),
    archived: z.enum(['true', 'false']).optional(),
    /**
     * Recibidos o enviados. Si no se dice nada: RECIBIDOS.
     *
     * El valor por defecto NO es «todo» a propósito. «Todos», «Sin leer» y «Sin
     * archivar» son filtros de la bandeja de ENTRADA, y meter ahí lo que tú
     * mandas convierte la bandeja en otra cosa. Los enviados tienen su pestaña.
     */
    direction: z.enum(['INBOUND', 'OUTBOUND']).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export const EmailListItemSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    accountEmail: z.string(),
    fromAddress: z.string(),
    fromEmail: z.string(),
    /** Destinatarios. En un correo ENVIADO son la única pista útil de la fila:
     *  el remitente eres tú en todos, y una lista de tu propio nombre no dice nada. */
    toAddresses: z.array(z.string()).default([]),
    direction: z.enum(['INBOUND', 'OUTBOUND']).default('INBOUND'),
    subject: z.string().nullable(),
    snippet: z.string(),
    hasAttachments: z.boolean(),
    isRead: z.boolean(),
    isFlagged: z.boolean(),
    sentAt: z.string().datetime(),
    receivedAt: z.string().datetime(),
    archivedToClientId: z.string().nullable(),
    archivedToClientName: z.string().nullable(),
    archivedToOpportunityId: z.string().nullable(),
    archivedToOpportunityCode: z.string().nullable(),
});
export const EmailListResponseSchema = z.object({
    items: z.array(EmailListItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
export const EmailAttachmentDtoSchema = z.object({
    id: z.string(),
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int(),
    inline: z.boolean(),
});
export const EmailDetailSchema = EmailListItemSchema.extend({
    toAddresses: z.array(z.string()),
    ccAddresses: z.array(z.string()),
    bccAddresses: z.array(z.string()),
    replyToAddress: z.string().nullable(),
    bodyUrl: z.string().nullable(),
    attachments: z.array(EmailAttachmentDtoSchema),
});
/** F43 — Cuerpo completo del correo, parseado del .eml bajo demanda. */
export const EmailContentSchema = z.object({
    /** HTML saneado con imágenes cid reescritas; null si el correo es solo texto. */
    html: z.string().nullable(),
    text: z.string().nullable(),
    /** Message-ID del correo (para In-Reply-To al responder). */
    messageIdHeader: z.string().nullable(),
    /** Cadena References del hilo. */
    references: z.array(z.string()),
});
export const ArchiveEmailSchema = z
    .object({
    clientId: z.string().optional().nullable(),
    opportunityId: z.string().optional().nullable(),
})
    .refine((d) => d.clientId || d.opportunityId, {
    message: 'Indica al menos un cliente o oportunidad',
});
//# sourceMappingURL=emails.js.map